import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** An appointment 038 assumed was a guardianship, on the review list. */
export interface KindReviewRow {
  appointment_id: string;
  client_id: string;
  client_ref: string;
  display_name: string;
  legal_status: string;
  closed: boolean;
  kind_code: string;
  kind_label: string;
  /** NULL when the appointment was put in doubt by name and the client has no scope (068). */
  scope_code: string | null;
  scope_label: string | null;
  /** Why the kind is in doubt: a named doubt (068), else why the scope leaves it open (038). */
  why: string | null;
  index_number: string | null;
  start_date: string | null;
  is_temporary: boolean;
  end_date: string | null;
  visits_owed_now: number | null;
  visit_source: string | null;
  visit_exempt: boolean;
}

export interface KindChoice {
  code: string;
  label: string;
  owes_visits: boolean;
  holds_a_side: boolean;
  needs_commission: boolean;
}

/** The kind review. Tiers 1–2, the Director of Legal, the Paralegal, the Director of Compliance; the route decides. */
export function useKindReview(enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['appointments', 'kind-review'],
    queryFn: () =>
      apiFetch<{ appointments: KindReviewRow[]; live: number; closed: number; kinds: KindChoice[] }>(
        '/appointments/kind-review',
        getToken,
      ),
    ...queryDefaults,
    enabled,
  });
}

export function useRecordKind() {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (a: { appointmentId: string; kind_code: string }) =>
      apiFetch<{
        appointment: { appointment_id: string; client_id: string; kind_code: string };
        client: { legal_status: string; visits_per_year: number | null; visit_source: string | null } | null;
      }>(`/appointments/${a.appointmentId}/kind`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ kind_code: a.kind_code }),
      }),
    onSuccess: () => {
      // The kind moves the visit obligation and the commission denominator.
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
      void queryClient.invalidateQueries({ queryKey: ['commissions'] });
      void queryClient.invalidateQueries({ queryKey: ['client'] });
    },
  });
}
